import Vue from 'vue'
import merge from 'lodash/merge'
import applogger from '@applogger'
import getLocalTasks from './local-tasks'

import { NotFoundError } from 'app/errors'

const state = () => ({
  nodes: {},
  productSettings: {},
  localtasks: {},
  attachedViews: {},
  paths: {},
  node_access: {},
  node_lookups: {},
  commonitemslookup: {},
  access: {},
  promises: {}
})

const actions = {
  // Load the cached version of all common pages for anonymous access and the lookup of the NID to the url alias
  // - this will reduce the need for individual item lookups for all common pages within the site.
  loadCommonItemsLookup({ commit, state, rootGetters }, payload) {
    payload = {
      account: 0,
      ...payload
    }

    if (state.commonitemslookup[payload.account] && Object.keys(state.commonitemslookup[payload.account]).length > 0) {
      return Promise.resolve(true)
    }

    const cachePath = `/vuejs/item-lookup-bulk/${payload.account}`

    if (state.promises[cachePath] !== undefined) {
      return state.promises[cachePath]
    }

    let requestConfig = rootGetters['AuthorizedRequestConfig']({ noauth: payload.account === 0 })

    state.promises[cachePath] = this.dispatch('loadItemFromRemote', { url: cachePath, config: requestConfig })
      .then(resp => {
        // /vuejs/item-lookup-bulk/<account> wraps its payload one level
        // deeper than every sibling loadItemFromRemote endpoint (compare
        // /vuejs/filestores: {status, data: {...payload}, timestamp} vs
        // this one: {status, data: {data: {...payload}}, timestamp}) - a
        // backend inconsistency, not a frontend choice. Reading
        // resp.data.lookupcontentitemscommon (the normal single-level
        // convention) always resolved undefined, which meant this never
        // actually populated state.commonitemslookup - so it was silently
        // dropped from the SSR-serialized initial state (JSON drops
        // undefined values) and the "already loaded" guard above always
        // missed, forcing a redundant re-fetch on every client hydration.
        commit('SET_commonItems_LOOKUP', {
          account: payload.account,
          data: resp.data.data.lookupcontentitemscommon
        })
        delete state.promises[cachePath]
      })
      .catch((err) => {
        console.log('Common items failed to load: ', err)
      })

    return state.promises[cachePath]
  },
  load({ commit, state, rootGetters }, payload) {
    let nid = payload.nid

    let cachePath
    let matches
    if ((matches = nid.match(/\/node\/(\d+)/))) {
      nid = matches[1]
    }
    if (!isNaN(parseFloat(nid)) && isFinite(nid) && state.nodes[nid] !== undefined) {
      return state.nodes[nid].data.node
    }
    else if (state.paths[payload.nid] !== undefined) {
      const n = state.nodes[state.paths[payload.nid]].data.node
      return Promise.resolve(n)
    }
    if (isNaN(nid) && nid.startsWith('/')) {
      cachePath = '/vuejs/node' + nid
    }
    else {
      cachePath = '/vuejs/node/' + nid
    }

    if (state.promises[cachePath] !== undefined) {
      return state.promises[cachePath]
    }
    const config = rootGetters.AuthorizedRequestConfig()
    state.promises[cachePath] = this.dispatch('loadItemFromRemote', { url: cachePath, config })
      .then(cacheData => {
        commit('storeNode', cacheData)
        delete state.promises[cachePath]
        return cacheData.data.node
      })
      .catch((err) => {
        console.log('Node failed to load: ', err)
      })
    return state.promises[cachePath]
  },

  productSettings({ commit, state, rootGetters }, payload) {
    const stack = (new Error()).stack
    applogger.error(`Deprecated: node/productSettings is deprecated. please use product/productSettings getter`)
    applogger.error(stack)
    return Promise.resolve(rootGetters['products/productSettings'](payload.nid))
    // let nid = payload.nid
    // if (state.productSettings[nid] !== undefined) {
    //   return Promise.resolve(state.productSettings[nid])
    // }

    // const config = rootGetters.AuthorizedRequestConfig()
    // config.validateStatus = function(status) {
    //   return status >= 200 && status < 500 // default
    // }
    // return this.dispatch('loadItemFromRemote', { url: `/vuejs/ecommerce/product-settings/${nid}`, config })
    //   .then(productSettings => {
    //     commit('storeProductSettings', { nid: nid, data: productSettings.data })
    //     return productSettings.data
    //   },
    //   err => {
    //     if (err.status >= 400 && err.status < 500) {
    //       commit('storeProductSettings', { nid: nid, data: null })
    //     }
    //     else {
    //       applogger.error(`Unable to load product settings for ID: ${nid} - ${err.message}`)
    //     }
    //   })
  },

  productStock({ dispatch }, payload) {
    const stack = (new Error()).stack
    applogger.error(`Deprecated: node/productStock is deprecated. please use products/productStock action`)
    applogger.error(stack)
    return this.dispatch('products/currentStock', payload)
    // const nid = payload.nid
    // const url = rootState.registry.getServiceSetting('inventory', 'stock').concat('/', nid)
    // const config = rootGetters.AuthorizedRequestConfig()

    // return this.dispatch('loadItemFromRemote', { url, config })
    //   .then(productStock => {
    //     return productStock.data
    //   })
    //   .catch(err => {
    //     applogger.error(`Unable to load product stock for ID: ${nid} - ${err.message}`)
    //   })
  },

  attachedView({ commit, state }, payload) {
    let nid = payload.nid
    let cacheOptions = payload.cacheOptions

    if (state.attachedViews[nid] === undefined) {
      // Set default options
      let defaultOptions = {}
      cacheOptions = merge({}, cacheOptions, defaultOptions)
      return this.dispatch('loadItemFromRemote', { url: `/vuejs/node/${nid}/attached-view`, config: cacheOptions })
        .then(resp => {
          if (resp.data.html) {
            commit('storeAttachedView', {
              nid: nid,
              html: resp.data.html
            })
          }
          else {
            commit('storeAttachedView', {
              nid: nid,
              view: resp.data.view_name,
              display: resp.data.display_id
            })
            commit('list/storeListData', { view: resp.data.view_name, display: resp.data.display_id, list: resp.data.content })
          }
        })
        .catch(e => {
          commit('storeAttachedView', { nid: nid, html: '' })
        })
    }
  },

  lookupItem({ commit, state, getters, rootGetters }, payload) {
    if (payload.reset === undefined || !payload.reset) {
      if (getters['lookupItem'](payload.path)) {
        return getters['lookupItem'](payload.path)
      }
    }
    return new Promise((resolve, reject) => {
      // Lookup the path in the common item to see if it exists.
      const account = rootGetters['user/userid']
      const commonItems = state.commonitemslookup[Number(account)]
      const nodeIdLookup = payload.path.match(/\/node\/(\d+)/)
      let item
      if (nodeIdLookup) {
        item = commonItems.find(item => item.id === nodeIdLookup[1])
      }
      else {
        item = commonItems
          .filter(item => item.path)
          .find(item => item.path === payload.path)
      }
      if (item) {
        resolve(item)
      }
      else {
        reject(new Error(`lookupItem: ${item.id} from internal cache failed - load from remote`))
      }
    })
      .catch(_err => {
        return rootGetters['getGraphQLClient']().query({
          query: `query lookup($srcpath: String!) {
          item: lookupcontentitem(path: $srcpath) {
            ... on ContentInterface {
              id
              type
              path
              access
            }
          }
        }`,
          opName: 'lookup',
          variables: { srcpath: payload.path }
        })
          .then(response => {
            if (response.errors === undefined) {
              return response.data.item
            }
          })
      })
      .then(item => {
        if (!item) {
          throw new NotFoundError(`Unable to locate item for path: ${payload.path}`)
        }
        if (item.path) {
          item.path = item.path.substr(0, 1) === '/' ? item.path : '/' + item.path
        }
        commit('SET_ITEM_LOOKUP', item)
        commit('SET_ITEM_ACCESS', item)
        return item
      })
  },
  itemAccess({ commit, state, rootGetters }, payload) {
    if (payload.reset === undefined || !payload.reset) {
      if (state.node_access[payload.id] !== undefined) {
        return Promise.resolve(state.node_access[payload.id])
      }
    }

    let develaccess = rootGetters['user/checkPermissions']('developer access')
    if (!develaccess) {
      return rootGetters['getGraphQLClient']().query({
        query: 'query access($id: ID!) { itemperms(id: $id) }',
        opName: 'access',
        variables: { id: payload.id }
      })
        .then(res => {
          commit('SET_ITEM_ACCESS', { id: payload.id, access: res.data.itemperms })
          return res.data.itemperms
        })
    }
  },

  resetNode({ commit }, payload) {
    commit('removeNode', payload.nid)
    return Promise.resolve(payload.nid)
  },

  // The Reset Cache is called in the logout to remove all cached nodes and other related items from the Vuex Store
  resetCache({ commit }) {
    commit('resetCache')
  },
  generateLocalTasks({ dispatch, commit }, payload) {
    return Promise.all([
      dispatch('itemAccess', { id: payload.nid }),
      dispatch('load', { nid: payload.nid })
    ])
      .then(([_access, item]) => {
        let tasks = getLocalTasks(item, this, payload.router, payload.options || {})
        commit('UPDATE_LOCAL_TASKS', { item, tasks })
      })
  }
}

const mutations = {
  SET_commonItems_LOOKUP(state, value) {
    Vue.set(state.commonitemslookup, value.account, value.data)
  },
  RESET_ITEM_ACCESS_ON_AUTH(state) {
    Vue.set(state, 'node_access', {})
  },
  UPDATE_LOCAL_TASKS(state, payload) {
    Vue.set(state.localtasks, payload.item.nid, payload.tasks)
  },
  UPDATE_CONTENT_FLAGS(state, payload) {
    if (payload.status !== undefined) {
      Vue.set(state.nodes[payload.nid].data.node, 'status', payload.status ? '1' : '0')
    }
    if (payload.promote !== undefined) {
      Vue.set(state.nodes[payload.nid].data.node, 'promote', payload.promote ? '1' : '0')
    }
    if (payload.sticky !== undefined) {
      Vue.set(state.nodes[payload.nid].data.node, 'sticky', payload.sticky ? '1' : '0')
    }
  },
  SET_ITEM_ACCESS(state, opts) {
    state.node_access[opts.id] = ['view', 'update', 'delete', 'publish'].reduce((acc, op) => ({ ...acc, [op]: !!opts.access.includes(op) }), {})
  },
  SET_ITEM_LOOKUP(state, opts) {
    state.node_lookups[opts.path] = opts
    state.node_lookups[`/node/${opts.id}`] = opts
    state.node_lookups[`node--${opts.id}`] = opts
  },
  removeNode(state, nid) {
    Vue.delete(state.nodes, nid)
  },
  storeNode(state, node) {
    Object.keys(state.paths).forEach(path => {
      if (state.paths[path] == node.data.node.nid) { // eslint-disable-line
        Vue.delete(state.paths, path)
      }
    })
    Vue.set(state.nodes, node.data.node.nid, node)
    if (node.data.node.path) {
      Vue.set(state.paths, '/' + node.data.node.path, node.data.node.nid)
    }
  },
  storeProductSettings(state, data) {
    Vue.set(state.productSettings, data.nid, data.data)
  },
  storeAttachedView(state, data) {
    if (data.html) {
      Vue.set(state.attachedViews, data.nid, { html: data.html })
    }
    else {
      Vue.set(state.attachedViews, data.nid, { view: data.view, display: data.display })
    }
  },
  resetCache(state, date) {
    // state.nodes = {}
    // state.localtasks = {}
    // state.paths = {}
    // state.attachedViews = {}
    state.access = {}
    state.node_access = {}
  }
}

const getters = {
  itemAccess: (state, _getters, _rootState, rootGetters) => {
    return function(nid, mode) {
      try {
        let develaccess = rootGetters['user/checkPermissions']()
        if (mode !== undefined) {
          if (develaccess) {
            return true
          }
          else {
            return state.node_access[nid][mode]
          }
        }
        if (develaccess) {
          return Object.keys(state.node_access[nid]).reduce((acc, type) => ({ ...acc, [type]: true }), {})
        }
        else {
          return state.node_access[nid]
        }
      }
      catch (err) {
        applogger.error(`itemAccess Error: ${nid} - ${mode} - ${err.message}`)
        return false
      }
    }
  },
  lookupItem: (state) => {
    return function(path) {
      const newPath = path.substr(0, 1) === '/' ? path : '/' + path
      if (state.node_lookups[newPath]) {
        return state.node_lookups[newPath]
      }
      if (state.node_lookups[`node--${path}`]) {
        return state.node_lookups[`node--${path}`]
      }
      return null
    }
  },
  published: (state) => {
    return function(nid) {
      return (state.nodes[nid] && (parseInt(state.nodes[nid].data.node.status) === 1))
    }
  },
  featured: (state) => {
    return function(nid) {
      return (state.nodes[nid] && (parseInt(state.nodes[nid].data.node.promote) === 1))
    }
  },
  sticky: (state) => {
    return function(nid) {
      return (state.nodes[nid] && (parseInt(state.nodes[nid].data.node.sticky) === 1))
    }
  },

  getLocalTasks: (state) => {
    return function(nid) {
      return state.localtasks[nid]
    }
  },

  content_body: (state) => {
    return function(nid) {
      return (state.nodes[nid] && state.nodes[nid].body) || ''
    }
  },
  getProductSettings(state) {
    return (nid) => {
      if (state.nodes[nid] && state.nodes[nid].data.node.ptype) {
        return state.nodes[nid].data.node.productsettings
      }
    }
  },
  getByNID: (state) => {
    return (nid) => {
      return (state.nodes[nid]) ? state.nodes[nid].data.node : null
    }
  },
  getByPath: (state) => {
    return function(path) {
      let nid = state.paths[path]
      if (nid && state.nodes[nid]) {
        return state.nodes[nid].data.node
      }
      if ((nid = path.match(/^\/node\/(\d+)/))) {
        nid = nid[1]
        return (state.nodes[nid]) ? state.nodes[nid].data.node : null
      }
    }
  },
  getAttachedView: (state) => {
    return function(nid) {
      let v = state.attachedViews[nid]
      if (v === undefined) {
        return null
      }
      if (Object.prototype.hasOwnProperty.call(v, 'html')) {
        return v.html
      }
      else if (Object.prototype.hasOwnProperty.call(v, 'view') && Object.prototype.hasOwnProperty.call(v, 'display')) {
        return this['list/get'](v.view, v.display)
      }
      else {
        return null
      }
    }
  },
  commentCount: (state) => {
    return function(nid) {
      if (state.nodes[nid]) {
        if (state.nodes[nid].comment_count > 1 || state.nodes[nid].comment_count === 0) {
          return '1 comment'
        }
        else {
          return state.nodes[nid].comment_count + ' comments'
        }
      }
      return ''
    }
  },
  // The type/label for the Visibility of the content of the node.
  visibility: (state) => {
    return function(nid, raw = false) {
      if (!state.nodes[nid] || !Object.prototype.hasOwnProperty.call(state.nodes[nid], 'field_t_membership_visibility')) {
        return
      }
      if (raw) {
        return state.nodes[nid].field_t_membership_visibility
      }
      else {
        return this['config/fieldValue'](state.nodes[nid], 'field_t_membership_visibility')
      }
    }
  },
  // Is the node content visible for the currently logged in user
  visible: (state) => {
    return function(nid) {
      if (!state.nodes[nid]) {
        return false
      }
      if (!state.access[nid]) {
        let systemRoles = this['config/variable_get']('system_roles')
        if (!state.nodes[nid].field_t_membership_visibility) {
          state.access[nid] = true
        }
        else {
          switch (state.nodes[nid].field_t_membership_visibility) {
          case 'public':
            state.access[nid] = true
            break

          case 'private':
            state.access[nid] = this['user/isLoggedIn']
            break

          case 'member':
            state.access[nid] = this['user/hasAnyRole'](systemRoles)
            break

          default:
            state.access[nid] = true
            break
          }
        }
      }
      return state.access[nid]
    }
  }
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
}
